import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActionButton,
  ActionButtonIcons,
  Button,
  DataTable,
  InputField,
  Select,
  type DataTableColumn,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as AddIcon } from '@enamelai/design-system/icons/Add'
import {
  IPhaseItems,
  IPhases,
  TxPlanProcedureModalContext,
} from '@dataTypes/treatmentPlan.types'
import { InsurancePlan } from '@dataTypes/insurance.types'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import { formatNumber } from '@utils/numberFormatter.util'
import {
  getTSFromToothSurfaces,
  parseSurfaceString,
  stringifyTxPlanItemDragPayload,
  TX_PLAN_ITEM_DRAG_MIME,
  txPlanProcedureProviderHighlightRowKey,
} from '@utils/treatment-plan.utils'
import { formatToothSurfaceEntriesForDisplay } from '@utils/procedure-surface-selection.utils'
import { compareChartHistorySortValues } from '@utils/charting-history-table-sort.util'

const getTxPlanFeeEditKey = (
  tabKey: string,
  phaseOriginalIdx: number,
  itemId: string,
): string => `${tabKey}:${phaseOriginalIdx}:${itemId}`

interface TxPlanFeeEditCellProps {
  charges: number
  isEditMode: boolean
  allowManualFeeOverride: boolean
  isActiveEditor: boolean
  initialDraftValue?: string
  onStartEdit: () => void
  onDraftChange: (draftValue: string) => void
  onFinishEdit: (save: boolean) => void
}

type TxPlanFeeEditSession = {
  key: string
  item: IPhaseItems
  charges: number
  draftValue: string
}

type TxPlanProcedureRow = DataTableRow &
  IPhaseItems & {
    id: string
    code: string
    description: string
    allowed_amt: number
    write_off: number
  }

const parseClinicFeeDraft = (draftValue: string): number | null => {
  const parsedFee = Number.parseFloat(draftValue)
  return Number.isNaN(parsedFee) || parsedFee < 0 ? null : parsedFee
}

const TxPlanFeeEditCell = memo(
  ({
    charges,
    isEditMode,
    allowManualFeeOverride,
    isActiveEditor,
    initialDraftValue,
    onStartEdit,
    onDraftChange,
    onFinishEdit,
  }: TxPlanFeeEditCellProps): React.ReactElement => {
    const [draftValue, setDraftValue] = useState(
      () => initialDraftValue ?? String(charges),
    )
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

    useEffect(() => {
      if (isActiveEditor) {
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }, [isActiveEditor])

    if (!isEditMode || !allowManualFeeOverride) {
      return <>{formatNumber(charges, true)}</>
    }

    if (isActiveEditor) {
      return (
        <div className="tx-plan-fee-edit d-flex align-items-center gap-1">
          <InputField
            ref={inputRef}
            variant="text"
            label=""
            className="tx-plan-fee-edit-input"
            aria-label="Clinic fee"
            value={draftValue}
            clearable={false}
            onClick={(e) => e.stopPropagation()}
            onChange={(
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ): void => {
              setDraftValue(e.target.value)
              onDraftChange(e.target.value)
            }}
            onKeyDown={(e) => {
              e.stopPropagation()
              if (e.key === 'Enter') {
                onFinishEdit(true)
              } else if (e.key === 'Escape') {
                onFinishEdit(false)
              }
            }}
            onBlur={() => onFinishEdit(true)}
          />
        </div>
      )
    }

    return (
      <div className="tx-plan-fee-edit d-flex align-items-center gap-1">
        <span className="tx-plan-fee-edit-label">
          {formatNumber(charges, true)}
        </span>
        <button
          type="button"
          className="unstyled-button mgc_pencil_fill tx-plan-fee-edit-icon"
          aria-label="Edit clinic fee"
          title="Edit clinic fee"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation()
            setDraftValue(String(charges))
            onStartEdit()
          }}
        />
      </div>
    )
  },
)

TxPlanFeeEditCell.displayName = 'TxPlanFeeEditCell'

function providerSelectOptionsForRow(
  baseOptions: Provider[],
  lookupProviders: Provider[],
  selectedProviderId?: string | null,
): Provider[] {
  const id =
    typeof selectedProviderId === 'string' &&
    selectedProviderId.trim().length > 0
      ? selectedProviderId
      : null
  if (!id) return baseOptions
  if (baseOptions.some((p) => p.provider_id === id)) return baseOptions
  const fallback = lookupProviders.find((p) => p.provider_id === id)
  return fallback !== undefined ? [...baseOptions, fallback] : baseOptions
}

export type TxPlanPhaseFormState = {
  planName: string
  insuranceProvider: string | null
  selectedInsurance: InsurancePlan | null
  selectedDiscountId: string
  customDiscount: number
  phases: IPhases[]
  activeAccordionKeys: string[]
  procedureModal: TxPlanProcedureModalContext | null
}

export type TxPlanProcedureProviderChangeArgs = {
  tabKey: string
  phaseOriginalIdx: number
  item: IPhaseItems
  providerId: string
}

export type TxPlanProcedureFeeChangeArgs = {
  tabKey: string
  phaseOriginalIdx: number
  item: IPhaseItems
  clinicFee: number
}

export type TxPlanPhaseProcedureGridInteractionRef = {
  saveHistorySnapshot: (tabKey: string) => void
  editToothSelection: (
    tabKey: string,
    phaseOriginalIdx: number,
    item: IPhaseItems,
  ) => void
  clearTreatmentPlanDragUi: () => void
}

export interface TxPlanPhaseProcedureGridProps {
  tabKey: string
  originalIdx: number
  visibleItems: IPhaseItems[]
  isEditMode: boolean
  draggingItemId: string | null
  providerList: Provider[]
  /** Full staff list for resolving saved provider_id in read-only mode */
  providerLookupList?: Provider[]
  /** Row keys (from txPlanProcedureProviderHighlightRowKey) flagged after save validation */
  missingProviderRowKeys?: ReadonlySet<string>
  allowManualFeeOverride?: boolean
  showInsuranceEstimate?: boolean
  onProcedureProviderChange: (args: TxPlanProcedureProviderChangeArgs) => void
  onProcedureFeeChange?: (args: TxPlanProcedureFeeChangeArgs) => void
  setFormStates: React.Dispatch<
    React.SetStateAction<Record<string, TxPlanPhaseFormState>>
  >
  setDraggingItemId: React.Dispatch<React.SetStateAction<string | null>>
  interactionRef: React.MutableRefObject<TxPlanPhaseProcedureGridInteractionRef>
  enableVirtualization?: boolean
}

const TxPlanPhaseProcedureGridInner = ({
  tabKey,
  originalIdx,
  visibleItems,
  isEditMode,
  draggingItemId,
  providerList,
  providerLookupList = providerList,
  missingProviderRowKeys,
  allowManualFeeOverride = false,
  showInsuranceEstimate = true,
  onProcedureProviderChange,
  onProcedureFeeChange,
  setFormStates,
  setDraggingItemId,
  interactionRef,
  enableVirtualization = false,
}: TxPlanPhaseProcedureGridProps): React.ReactElement => {
  const [editingFeeKey, setEditingFeeKey] = useState<string | null>(null)
  const [sortColumnId, setSortColumnId] = useState<string | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')
  // Template cells remount (without new props) whenever `columns` rebuilds, so the open fee edit lives here.
  const feeEditRef = useRef<TxPlanFeeEditSession | null>(null)
  const feeSaveTargetRef = useRef({ tabKey, originalIdx, onProcedureFeeChange })
  feeSaveTargetRef.current = { tabKey, originalIdx, onProcedureFeeChange }

  const startFeeEdit = useCallback(
    (key: string, item: IPhaseItems, charges: number): void => {
      feeEditRef.current = { key, item, charges, draftValue: String(charges) }
      setEditingFeeKey(key)
    },
    [],
  )

  const updateFeeDraft = useCallback(
    (key: string, draftValue: string): void => {
      const session = feeEditRef.current
      if (session && session.key === key) {
        session.draftValue = draftValue
      }
    },
    [],
  )

  const finishFeeEdit = useCallback((key: string, save: boolean): void => {
    const session = feeEditRef.current
    if (!session || session.key !== key) return
    feeEditRef.current = null
    setEditingFeeKey(null)
    const clinicFee = save ? parseClinicFeeDraft(session.draftValue) : null
    if (clinicFee === null || clinicFee === session.charges) return
    const target = feeSaveTargetRef.current
    target.onProcedureFeeChange?.({
      tabKey: target.tabKey,
      phaseOriginalIdx: target.originalIdx,
      item: session.item,
      clinicFee,
    })
  }, [])

  // The grid unmounts while the plan tab is hidden; keep an in-progress fee instead of dropping it.
  useEffect(
    () => (): void => {
      const session = feeEditRef.current
      if (session) finishFeeEdit(session.key, true)
    },
    [finishFeeEdit],
  )

  const rowModels = useMemo((): TxPlanProcedureRow[] => {
    return visibleItems.map(
      (p) =>
        ({
          ...p,
          id: p.item_id,
          code: p.procedure_settings?.procedure?.procedure_code ?? '',
          description: p.procedure_settings?.procedure?.description ?? '',
          allowed_amt: p.allowed_amount ?? 0,
          write_off: p.write_off ?? 0,
        }) as unknown as TxPlanProcedureRow,
    )
  }, [visibleItems])

  const sortedRows = useMemo((): TxPlanProcedureRow[] => {
    if (!sortColumnId) return rowModels
    return [...rowModels].sort((left, right) =>
      compareChartHistorySortValues(
        (left[sortColumnId] ?? '') as string | number,
        (right[sortColumnId] ?? '') as string | number,
        sortDirection,
      ),
    )
  }, [rowModels, sortColumnId, sortDirection])

  const columns = useMemo((): DataTableColumn<TxPlanProcedureRow>[] => {
    const toothSurfaceTemplate = (
      props: TxPlanProcedureRow,
    ): React.ReactNode => {
      const toothSurfaceEntries = getTSFromToothSurfaces(props.tooth_surfaces)
      const surfaceEntries = parseSurfaceString(props.surface)
      const parsedEntries =
        toothSurfaceEntries.length > 0 ? toothSurfaceEntries : surfaceEntries
      const hasNoAreaType =
        parsedEntries.length === 0 ||
        parsedEntries.every((e) => e.area_type === null)

      const showEditPencil =
        isEditMode && !hasNoAreaType && parsedEntries.length > 0

      return (
        <div className="tx-plan-fee-edit template-cell-tooth-surface-wrapper">
          <span
            className={`template-cell-tooth-surface tx-plan-fee-edit-label ${
              hasNoAreaType && isEditMode
                ? 'template-cell-tooth-surface-empty'
                : ''
            }`}
          >
            {hasNoAreaType ? (
              isEditMode ? (
                <Button
                  type="button"
                  variant="text"
                  size="xs"
                  className="add-ts-button"
                  leftIcon={<AddIcon width={16} height={16} aria-hidden />}
                  onClick={() =>
                    interactionRef.current.editToothSelection(
                      tabKey,
                      originalIdx,
                      props,
                    )
                  }
                >
                  Add T/S
                </Button>
              ) : null
            ) : (
              formatToothSurfaceEntriesForDisplay(parsedEntries)
            )}
          </span>
          {showEditPencil && (
            <button
              type="button"
              aria-label="Edit tooth/surface"
              title="Edit tooth/surface"
              className="unstyled-button mgc_pencil_fill tx-plan-fee-edit-icon"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                interactionRef.current.editToothSelection(
                  tabKey,
                  originalIdx,
                  props,
                )
              }}
            />
          )}
        </div>
      )
    }

    const builtColumns: DataTableColumn<TxPlanProcedureRow>[] = [
      ...(isEditMode
        ? [
            {
              id: '__tx_drag',
              label: '',
              width: 30,
              renderCell: (props: TxPlanProcedureRow): React.ReactNode => (
                <span
                  tabIndex={0}
                  className={`tx-plan-row-drag-handle${
                    draggingItemId === props.item_id ? ' dragging' : ''
                  }`}
                  title="Drag to reorder or move to another phase"
                  aria-label="Drag to reorder or move to another phase"
                  draggable={true}
                  onMouseDown={(ev: React.MouseEvent): void => {
                    ev.stopPropagation()
                  }}
                  onDragStart={(ev: React.DragEvent): void => {
                    ev.dataTransfer.setData(
                      TX_PLAN_ITEM_DRAG_MIME,
                      stringifyTxPlanItemDragPayload({
                        itemId: props.item_id,
                        fromPhaseOriginalIdx: originalIdx,
                      }),
                    )
                    ev.dataTransfer.effectAllowed = 'move'
                    const draggedId = props.item_id
                    window.setTimeout(() => {
                      setDraggingItemId(draggedId)
                    }, 0)
                    ev.stopPropagation()
                  }}
                  onDragEnd={(): void => {
                    window.setTimeout(() => {
                      interactionRef.current.clearTreatmentPlanDragUi()
                    }, 0)
                  }}
                  onKeyDown={(ev: React.KeyboardEvent): void => {
                    ev.preventDefault()
                  }}
                >
                  <i className="mgc mgc_dots_fill" aria-hidden />
                </span>
              ),
            },
          ]
        : []),
      {
        id: 'code',
        label: 'Code',
        sortable: true,
        width: '7%',
      },
      {
        id: 'description',
        label: 'Description',
        sortable: true,
        width: '21%',
        renderCell: (props): React.ReactNode => (
          <span title={props.description}>{props.description}</span>
        ),
      },
      {
        id: 'provider',
        label: 'Provider',
        width: '15%',
        renderCell: isEditMode
          ? (props: TxPlanProcedureRow) => {
              const rowKey = txPlanProcedureProviderHighlightRowKey(
                originalIdx,
                props,
              )
              const showProviderError =
                missingProviderRowKeys?.has(rowKey) ?? false
              return (
                <div
                  className={
                    showProviderError
                      ? 'tx-plan-provider-select-wrap tx-plan-provider-select-wrap--error'
                      : 'tx-plan-provider-select-wrap'
                  }
                >
                  <Select
                    label=""
                    placeholder="Select Provider"
                    error={showProviderError}
                    value={props.provider_id ?? ''}
                    options={providerSelectOptionsForRow(
                      providerList,
                      providerLookupList,
                      props.provider_id,
                    ).map((option) => ({
                      value: option.provider_id,
                      label: `Dr. ${getFullName(option)}`,
                    }))}
                    onChange={(nextValue): void => {
                      if (typeof nextValue !== 'string') {
                        return
                      }
                      onProcedureProviderChange({
                        tabKey,
                        phaseOriginalIdx: originalIdx,
                        item: props,
                        providerId: nextValue,
                      })
                    }}
                  />
                </div>
              )
            }
          : (props: TxPlanProcedureRow) => {
              const provider = providerLookupList.find(
                (p) => p.provider_id === props.provider_id,
              )
              return provider ? (
                <span>{`Dr. ${getFullName(provider)}`}</span>
              ) : (
                <span>-</span>
              )
            },
      },
      {
        id: 'surface',
        label: 'T/S',
        width: '8%',
        renderCell: isEditMode
          ? toothSurfaceTemplate
          : (props: TxPlanProcedureRow) => {
              const toothSurfaceEntries = getTSFromToothSurfaces(
                props.tooth_surfaces,
              )
              const surfaceEntries = parseSurfaceString(props.surface)
              const parsedEntries =
                toothSurfaceEntries.length > 0
                  ? toothSurfaceEntries
                  : surfaceEntries
              if (parsedEntries.length === 0) return <span>-</span>

              return (
                <div className="template-cell-tooth-surface">
                  {formatToothSurfaceEntriesForDisplay(parsedEntries)}
                </div>
              )
            },
      },
      {
        id: 'charges',
        label: 'Clinic fee',
        sortable: true,
        width: '6.5%',
        renderCell: (props: TxPlanProcedureRow) => {
          const feeEditKey = getTxPlanFeeEditKey(
            tabKey,
            originalIdx,
            props.item_id,
          )
          const charges = props.charges ?? 0
          const isActiveEditor = editingFeeKey === feeEditKey
          return (
            <TxPlanFeeEditCell
              charges={charges}
              isEditMode={isEditMode}
              allowManualFeeOverride={
                allowManualFeeOverride && Boolean(onProcedureFeeChange)
              }
              isActiveEditor={isActiveEditor}
              initialDraftValue={
                isActiveEditor ? feeEditRef.current?.draftValue : undefined
              }
              onStartEdit={() => startFeeEdit(feeEditKey, props, charges)}
              onDraftChange={(draftValue) =>
                updateFeeDraft(feeEditKey, draftValue)
              }
              onFinishEdit={(save) => finishFeeEdit(feeEditKey, save)}
            />
          )
        },
      },
      ...(showInsuranceEstimate
        ? [
            {
              id: 'allowed_amt',
              label: 'Allowed amt.',
              width: '6.5%',
              renderCell: (props: TxPlanProcedureRow): React.ReactNode => (
                <span>{formatNumber(props.allowed_amount ?? 0, true)}</span>
              ),
            },
            {
              id: 'write_off',
              label: 'Write-off',
              width: '6.5%',
              renderCell: (props: TxPlanProcedureRow): React.ReactNode => (
                <span>{formatNumber(props.write_off ?? 0, true)}</span>
              ),
            },
          ]
        : []),
      {
        id: 'adjustments',
        label: 'Discount',
        width: '6.5%',
        renderCell: (props: TxPlanProcedureRow) => (
          <span>{formatNumber(props.adjustments ?? 0, true)}</span>
        ),
      },
      ...(showInsuranceEstimate
        ? [
            {
              id: 'insurance',
              label: 'Est. ins. pay',
              width: '8%',
              renderCell: (props: TxPlanProcedureRow): React.ReactNode => (
                <span>{formatNumber(props.insurance ?? 0, true)}</span>
              ),
            },
            {
              id: 'outstanding',
              label: 'Est. pt. res.',
              width: '8%',
              renderCell: (props: TxPlanProcedureRow): React.ReactNode => (
                <span>{formatNumber(props.outstanding ?? 0, true)}</span>
              ),
            },
          ]
        : []),
    ]
    return builtColumns
  }, [
    isEditMode,
    draggingItemId,
    originalIdx,
    tabKey,
    providerList,
    providerLookupList,
    missingProviderRowKeys,
    allowManualFeeOverride,
    showInsuranceEstimate,
    editingFeeKey,
    startFeeEdit,
    updateFeeDraft,
    finishFeeEdit,
    onProcedureProviderChange,
    onProcedureFeeChange,
    setFormStates,
    setDraggingItemId,
    interactionRef,
  ])

  return (
    <DataTable<TxPlanProcedureRow>
      columns={columns}
      rows={sortedRows}
      className={
        enableVirtualization
          ? 'tx-plan-phase-data-table tx-plan-phase-data-table--large'
          : 'tx-plan-phase-data-table'
      }
      sortColumnId={sortColumnId ?? undefined}
      sortDirection={sortColumnId ? sortDirection : undefined}
      onSort={(columnId): void => {
        if (sortColumnId !== columnId) {
          setSortColumnId(columnId)
          setSortDirection('asc')
        } else if (sortDirection === 'asc') {
          setSortDirection('desc')
        } else {
          setSortColumnId(null)
          setSortDirection('asc')
        }
      }}
      showActions={isEditMode}
      actionsLabel=""
      renderActions={(props): React.ReactNode =>
        isEditMode ? (
          <ActionButton
            icon={ActionButtonIcons.delete()}
            label="Remove procedure"
            size="sm"
            tone="danger"
            variant="outline"
            onClick={(): void => {
              interactionRef.current.saveHistorySnapshot(tabKey)
              setFormStates((prev) => {
                const prevState = prev[tabKey]
                if (!prevState) return prev
                return {
                  ...prev,
                  [tabKey]: {
                    ...prevState,
                    phases: prevState.phases.map((phase, index) =>
                      index === originalIdx
                        ? {
                            ...phase,
                            items: phase.items.map((item: IPhaseItems) => ({
                              ...item,
                              is_hidden:
                                item.item_id === props.item_id
                                  ? true
                                  : item.is_hidden,
                            })),
                          }
                        : phase,
                    ),
                  },
                }
              })
            }}
          />
        ) : null
      }
    />
  )
}

const TxPlanPhaseProcedureGrid = memo(TxPlanPhaseProcedureGridInner)

export default TxPlanPhaseProcedureGrid
